import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { ABILITY_DEFS } from '../sim/abilities/AbilityDefs';
import type { SkillChoice } from '../sim/run/skillOffers';

const CARD_W = 250;
const CARD_H = 170;
const GAP = 24;

/**
 * Pick-1-of-3 skill card screen (bar threshold crossings). Pauses the sim
 * beneath (blocksUpdateBelow). Rerolls are unlimited but gem-priced at an
 * escalating cost; banishes (3/run, free) strike the focused card's skill
 * from all future offers. Early offers are additionally weighted forgiving
 * (skillOffers).
 */
export class LevelUpOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private cards: Container[] = [];
  private focusRing = new Graphics();
  private acceptDelay = 8;
  private cardHolder = new Container();
  private hint!: Text;

  constructor(
    private readonly ctx: GameContext,
    private choices: SkillChoice[],
    private readonly onPick: (choice: SkillChoice) => void,
    /** Current reroll price in gems, or null if the wallet can't cover it. */
    private readonly rerollPrice: () => { cost: number; affordable: boolean },
    /** Pays and rerolls; returns the fresh offer (never called unaffordable). */
    private readonly onReroll: () => SkillChoice[],
    private readonly banishesLeft: () => number,
    /** Banishes the given card's skill and rerolls; fresh offer (may be []). */
    private readonly onBanish: (choice: SkillChoice) => SkillChoice[],
  ) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.8 }));

    const title = new Text({
      text: 'CHOOSE',
      style: {
        fontFamily: 'monospace', fontSize: 30, fontWeight: 'bold',
        fill: Palette.gem, letterSpacing: 4,
      },
    });
    title.anchor.set(0.5);
    title.position.set(w / 2, h / 2 - CARD_H / 2 - 60);
    this.root.addChild(title);

    this.root.addChild(this.focusRing);
    this.root.addChild(this.cardHolder);
    this.buildCards();

    this.hint = new Text({
      text: '',
      style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim },
    });
    this.hint.anchor.set(0.5);
    this.hint.position.set(w / 2, h / 2 + CARD_H / 2 + 44);
    this.root.addChild(this.hint);
    this.refreshHint();
  }

  private refreshHint(): void {
    const { cost, affordable } = this.rerollPrice();
    const banishes = this.banishesLeft();
    this.hint.text = 'choose  [←→ / dpad]   confirm  [E / Ⓐ]' +
      `   reroll ◆${cost}${affordable ? '' : ' (broke)'}  [R / Ⓧ]` +
      (banishes > 0 ? `   banish ×${banishes}  [T / LB]` : '');
  }

  private buildCards(): void {
    const w = this.ctx.app.screen.width;
    const h = this.ctx.app.screen.height;
    this.cardHolder.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.cards = [];
    const total = this.choices.length * CARD_W + (this.choices.length - 1) * GAP;
    const x0 = (w - total) / 2;
    this.choices.forEach((choice, i) => {
      const card = this.buildCard(choice);
      card.position.set(x0 + i * (CARD_W + GAP), h / 2 - CARD_H / 2);
      this.cards.push(card);
      this.cardHolder.addChild(card);
    });
    this.focus = 0;
  }

  private buildCard(choice: SkillChoice): Container {
    const def = ABILITY_DEFS[choice.defId];
    const card = new Container();

    card.addChild(new Graphics()
      .roundRect(0, 0, CARD_W, CARD_H, 8)
      .fill(Palette.hudPanel)
      .stroke({
        width: 1.5,
        color: choice.isUpgrade ? Palette.gold : Palette.hudDim,
        alpha: 0.5,
      }));

    const tag = new Text({
      text: choice.isUpgrade ? `⬆ UPGRADES ${def.name.toUpperCase()}` : 'NEW SKILL',
      style: {
        fontFamily: 'monospace', fontSize: 11,
        fill: choice.isUpgrade ? Palette.gold : Palette.playerCool,
      },
    });
    tag.position.set(16, 14);
    card.addChild(tag);

    const name = new Text({
      text: choice.isUpgrade ? def.upgradeName : def.name,
      style: { fontFamily: 'monospace', fontSize: 20, fontWeight: 'bold', fill: Palette.hudText },
    });
    name.position.set(16, 36);
    card.addChild(name);

    const kind = new Text({
      text: def.tag,
      style: { fontFamily: 'monospace', fontSize: 11, fill: Palette.hudDim },
    });
    kind.position.set(16, 64);
    card.addChild(kind);

    const desc = new Text({
      text: choice.isUpgrade ? def.upgradeBlurb : def.desc(def.base, false),
      style: {
        fontFamily: 'monospace', fontSize: 13, fill: Palette.hudText,
        wordWrap: true, wordWrapWidth: CARD_W - 32, lineHeight: 19,
      },
    });
    desc.position.set(16, 96);
    card.addChild(desc);

    return card;
  }

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navX !== 0 && this.cards.length > 0) {
      this.focus = (this.focus + input.navX + this.cards.length) % this.cards.length;
    }
    if (input.rerollPressed && this.rerollPrice().affordable) {
      const fresh = this.onReroll();
      if (fresh.length > 0) {
        this.choices = fresh;
        this.buildCards();
      }
      this.refreshHint();
      return;
    }
    if (input.skipPressed && this.banishesLeft() > 0 && this.choices.length > 0) {
      const fresh = this.onBanish(this.choices[this.focus]!);
      if (fresh.length === 0) {
        // Nothing left to offer after the banish — the pick dissolves.
        this.ctx.scenes.pop();
        return;
      }
      this.choices = fresh;
      this.buildCards();
      this.refreshHint();
      return;
    }
    if (input.confirmPressed && this.choices.length > 0) {
      const choice = this.choices[this.focus]!;
      this.ctx.scenes.pop();
      this.onPick(choice);
    }
  }

  render(_alpha: number): void {
    const g = this.focusRing;
    g.clear();
    const card = this.cards[this.focus];
    if (!card) return;
    g.roundRect(card.position.x - 4, card.position.y - 4, CARD_W + 8, CARD_H + 8, 10)
      .stroke({ width: 2.5, color: Palette.focus });
  }
}
